import type { FastifyPluginAsync } from 'fastify'
import { analyticsService } from '../../services/analytics.service.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('AdminAnalyticsController')

export const adminAnalyticsRoutes: FastifyPluginAsync = async (fastify) => {
  
  /**
   * GET /admin/analytics/dashboard
   * Get complete analytics dashboard
   */
  fastify.get('/dashboard', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month', 'quarter', 'year'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const dashboard = await analyticsService.getDashboard(period)
      
      return reply.status(200).send({
        success: true,
        data: dashboard
      })
    } catch (error) {
      logger.error('[GET /dashboard] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get analytics dashboard'
      })
    }
  })

  /**
   * GET /admin/analytics/users
   * Get user analytics
   */
  fastify.get('/users', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month', 'quarter', 'year'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const analytics = await analyticsService.getUserAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /users] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get user analytics'
      })
    }
  })

  /**
   * GET /admin/analytics/revenue
   * Get revenue analytics
   */
  fastify.get('/revenue', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month', 'quarter', 'year'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const analytics = await analyticsService.getRevenueAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /revenue] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get revenue analytics'
      })
    }
  })

  /**
   * GET /admin/analytics/features
   * Get feature analytics
   */
  fastify.get('/features', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month', 'quarter', 'year'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const analytics = await analyticsService.getFeatureAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /features] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get feature analytics'
      })
    }
  })

  /**
   * GET /admin/analytics/ai
   * Get AI usage analytics
   */
  fastify.get('/ai', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const analytics = await analyticsService.getAIUsageAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /ai] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get AI analytics'
      })
    }
  })

  /**
   * GET /admin/analytics/system
   * Get system analytics
   */
  fastify.get('/system', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: any }
      const analytics = await analyticsService.getSystemAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /system] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get system analytics'
      })
    }
  })

  /**
   * GET /admin/analytics/extras
   * Activation cohorts, planner funnel, offline ratio, AI latency, routing errors, return-to-plan
   */
  fastify.get('/extras', async (_request, reply) => {
    try {
      const data = await analyticsService.getAdminExtras()
      return reply.status(200).send({ success: true, data })
    } catch (error) {
      logger.error('[GET /extras] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get admin extra analytics'
      })
    }
  })
}
